import AsyncStorage from '@react-native-async-storage/async-storage';
import * as FileSystem from 'expo-file-system/legacy';

class ImageUploadService {
  constructor() {
    this.baseURL = 'https://api.jobeng.ai';
    this.uploadEndpoints = [
      '/api/users/upload-avatar',
      '/api/users/avatar',
      '/api/profile/avatar',
      '/api/upload/avatar',
      '/api/user/profile/avatar'
    ];
  }

  /**
   * Upload image with multiple fallback strategies
   */
  async uploadImage(imageUri, options = {}) {
    const {
      onProgress = () => {},
      onSuccess = () => {},
      onError = () => {},
      enableLocalFallback = true
    } = options;

    try {
      onProgress(10, 'Starting upload...');

      // Get authentication token
      const token = await AsyncStorage.getItem('auth_token');
      
      if (!token) {
        throw new Error('No authentication token found');
      }

      onProgress(20, 'Preparing image...');

      // Try backend upload first
      const backendResult = await this.tryBackendUpload(imageUri, token, onProgress);
      
      if (backendResult.success) {
        onProgress(100, 'Upload complete!');
        onSuccess(backendResult);
        return backendResult;
      }

      // If backend fails and local fallback is enabled
      if (enableLocalFallback) {
        onProgress(80, 'Backend failed, saving locally...');
        const localResult = await this.saveImageLocally(imageUri);
        onProgress(100, 'Saved locally!');
        onSuccess(localResult);
        return localResult;
      }

      throw new Error('Upload failed and local fallback disabled');

    } catch (error) {
      console.error('Image upload error:', error);
      onError(error);
      throw error;
    }
  }

  /**
   * Try uploading to backend with multiple endpoints
   */
  async tryBackendUpload(imageUri, token, onProgress) {
    onProgress(30, 'Connecting to server...');

    // Create FormData
    const formData = new FormData();
    formData.append('avatar', {
      uri: imageUri,
      type: 'image/jpeg',
      name: 'avatar.jpg',
    });

    // Try each endpoint
    for (let i = 0; i < this.uploadEndpoints.length; i++) {
      const endpoint = this.uploadEndpoints[i];
      onProgress(40 + (i * 10), `Trying endpoint ${i + 1}...`);

      try {
        const response = await fetch(`${this.baseURL}${endpoint}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
          body: formData,
        });

        if (response.ok) {
          const data = await response.json();
          const avatarUrl = this.extractAvatarUrl(data);
          
          if (avatarUrl) {
            return {
              success: true,
              type: 'backend',
              url: avatarUrl,
              endpoint: endpoint,
              data: data
            };
          }
        }
      } catch (error) {
        console.log(`Endpoint ${endpoint} failed:`, error.message);
        continue;
      }
    }

    return { success: false, type: 'backend' };
  }

  /**
   * Extract avatar URL from various response formats
   */
  extractAvatarUrl(data) {
    // Try different possible response structures
    return (
      data.avatar ||
      data.url ||
      data.data?.avatar ||
      data.data?.url ||
      data.user?.avatar ||
      data.profile?.avatar ||
      null
    );
  }

  /**
   * Save image locally as fallback
   */
  async saveImageLocally(imageUri) {
    try {
      // Create a unique filename
      const timestamp = Date.now();
      const filename = `avatar_${timestamp}.jpg`;
      const localPath = `${FileSystem.documentDirectory}${filename}`;

      // Copy image to app's document directory
      await FileSystem.copyAsync({
        from: imageUri,
        to: localPath,
      });

      // Save reference in AsyncStorage
      await AsyncStorage.setItem('local_avatar_path', localPath);
      await AsyncStorage.setItem('local_avatar_timestamp', timestamp.toString());

      return {
        success: true,
        type: 'local',
        url: localPath,
        localPath: localPath,
        timestamp: timestamp
      };
    } catch (error) {
      console.error('Error saving image locally:', error);
      throw new Error('Failed to save image locally');
    }
  }

  /**
   * Get current avatar URL (local or remote)
   */
  async getCurrentAvatarUrl() {
    try {
      // First check if we have a remote URL
      const remoteAvatar = await AsyncStorage.getItem('remote_avatar_url');
      if (remoteAvatar) {
        return { type: 'remote', url: remoteAvatar };
      }

      // Then check for local avatar
      const localPath = await AsyncStorage.getItem('local_avatar_path');
      if (localPath) {
        // Check if file still exists
        const fileInfo = await FileSystem.getInfoAsync(localPath);
        if (fileInfo.exists) {
          return { type: 'local', url: localPath };
        } else {
          // Clean up broken reference
          await AsyncStorage.removeItem('local_avatar_path');
          await AsyncStorage.removeItem('local_avatar_timestamp');
        }
      }

      return { type: 'none', url: null };
    } catch (error) {
      console.error('Error getting current avatar:', error);
      return { type: 'none', url: null };
    }
  }

  /**
   * Remove avatar (both local and remote)
   */
  async removeAvatar() {
    try {
      const results = [];

      // Try to remove from backend
      try {
        const token = await AsyncStorage.getItem('auth_token');
        if (token) {
          const response = await fetch(`${this.baseURL}/api/users/remove-avatar`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });

          if (response.ok) {
            results.push({ type: 'backend', success: true });
          }
        }
      } catch (error) {
        console.log('Backend removal failed:', error.message);
        results.push({ type: 'backend', success: false, error: error.message });
      }

      // Remove local avatar
      try {
        const localPath = await AsyncStorage.getItem('local_avatar_path');
        if (localPath) {
          const fileInfo = await FileSystem.getInfoAsync(localPath);
          if (fileInfo.exists) {
            await FileSystem.deleteAsync(localPath);
          }
        }

        // Clean up AsyncStorage
        await AsyncStorage.removeItem('local_avatar_path');
        await AsyncStorage.removeItem('local_avatar_timestamp');
        await AsyncStorage.removeItem('remote_avatar_url');

        results.push({ type: 'local', success: true });
      } catch (error) {
        console.error('Local removal failed:', error);
        results.push({ type: 'local', success: false, error: error.message });
      }

      return {
        success: true,
        results: results
      };
    } catch (error) {
      console.error('Error removing avatar:', error);
      throw error;
    }
  }

  /**
   * Sync local images to backend when connection is restored
   */
  async syncLocalImages() {
    try {
      const localPath = await AsyncStorage.getItem('local_avatar_path');
      const timestamp = await AsyncStorage.getItem('local_avatar_timestamp');

      if (!localPath || !timestamp) {
        return { success: false, message: 'No local images to sync' };
      }

      // Check if file exists
      const fileInfo = await FileSystem.getInfoAsync(localPath);
      if (!fileInfo.exists) {
        // Clean up broken reference
        await AsyncStorage.removeItem('local_avatar_path');
        await AsyncStorage.removeItem('local_avatar_timestamp');
        return { success: false, message: 'Local image file not found' };
      }

      // Try to upload to backend
      const token = await AsyncStorage.getItem('auth_token');
      if (!token) {
        return { success: false, message: 'No authentication token' };
      }

      const backendResult = await this.tryBackendUpload(localPath, token, () => {});
      
      if (backendResult.success) {
        // Save remote URL and clean up local
        await AsyncStorage.setItem('remote_avatar_url', backendResult.url);
        await FileSystem.deleteAsync(localPath);
        await AsyncStorage.removeItem('local_avatar_path');
        await AsyncStorage.removeItem('local_avatar_timestamp');

        return {
          success: true,
          message: 'Local image synced to backend',
          url: backendResult.url
        };
      }

      return { success: false, message: 'Backend upload failed' };
    } catch (error) {
      console.error('Error syncing local images:', error);
      return { success: false, message: error.message };
    }
  }

  /**
   * Get upload statistics
   */
  async getUploadStats() {
    try {
      const localPath = await AsyncStorage.getItem('local_avatar_path');
      const remoteUrl = await AsyncStorage.getItem('remote_avatar_url');
      const timestamp = await AsyncStorage.getItem('local_avatar_timestamp');

      return {
        hasLocal: !!localPath,
        hasRemote: !!remoteUrl,
        localTimestamp: timestamp ? parseInt(timestamp) : null,
        needsSync: !!localPath && !remoteUrl
      };
    } catch (error) {
      console.error('Error getting upload stats:', error);
      return {
        hasLocal: false,
        hasRemote: false,
        localTimestamp: null,
        needsSync: false
      };
    }
  }
}

export default new ImageUploadService();
